import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CLIENT_HAS_FORFAITS_ERROR, useClient } from "../../hooks/useClient";
import { useContact } from "../../hooks/useContact";
import { useForfait } from "../../hooks/useForfait";
import { useIntervention } from "../../hooks/useIntervention";
import { useSite } from "hooks/useSite";

interface ModalComponentProps {
  isOpen: boolean;
  onClose: () => void;
  idDelete: string;
  field: string;
  type: string;
  newdata: (data: ClientType[] | ContactType[] | InterventionType[]) => void;
}

function ModalDelete(props: ModalComponentProps): JSX.Element {
  const { getClients, deleteClient } = useClient();
  const { getContacts, deleteContact, getContactIdsByClient } = useContact();
  const { getClientForfaits } = useForfait();
  const { fetch, deleteIntervention } = useIntervention();
  const { getSiteIdsByClient } = useSite();
  // Données rattachées au client à supprimer (null = en cours de chargement)
  const [clientLinks, setClientLinks] = useState<{
    forfaits: number;
    contactIds: string[];
    siteIds: string[];
  } | null>(null);
  const [linksError, setLinksError] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    setDeleteError(null);
    if (props.type !== "client") {
      return;
    }
    let isMounted = true;
    setClientLinks(null);
    setLinksError(false);
    const fetchLinks = async () => {
      try {
        const [forfaits, contactIds, siteIds] = await Promise.all([
          getClientForfaits(props.idDelete),
          getContactIdsByClient(props.idDelete),
          getSiteIdsByClient(props.idDelete),
        ]);
        if (isMounted) {
          setClientLinks({
            forfaits: forfaits ? forfaits.length : 0,
            contactIds,
            siteIds,
          });
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLinksError(true);
        }
      }
    };
    fetchLinks();
    return () => {
      isMounted = false;
    };
  }, [props.isOpen, props.idDelete, props.type, getClientForfaits, getContactIdsByClient, getSiteIdsByClient]);

  // Un client avec des forfaits porte l'historique des interventions : on ne le supprime pas
  const clientHasForfaits = props.type === "client" && clientLinks?.forfaits > 0;
  const canDelete =
    props.type !== "client" || (clientLinks !== null && !clientHasForfaits);

  async function handleDelete(id: string) {
    if (!canDelete || isDeleting) {
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      let newData: ClientType[] | ContactType[] | InterventionType[];
      if (props.type === "client") {
        await deleteClient(id, clientLinks.contactIds, clientLinks.siteIds);
        newData = await getClients();
      } else if (props.type === "contact") {
        await deleteContact(id);
        newData = await getContacts();
      } else if (props.type === "intervention") {
        await deleteIntervention(id);
        newData = await fetch();
      }
      props.onClose();
      props.newdata(newData);
    } catch (error) {
      console.error("Error adding data to Firebase:", error);
      if ((error as Error)?.name === CLIENT_HAS_FORFAITS_ERROR) {
        // Un forfait a été ajouté depuis l'ouverture de la fenêtre
        setClientLinks((links) => links && { ...links, forfaits: Math.max(links.forfaits, 1) });
      } else {
        setDeleteError("La suppression a échoué. Réessayez.");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Confirmation de suppression</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {props.type === "client" && (
            <span>Voulez-vous supprimer le client : {props.field}</span>
          )}
          {props.type === "contact" && (
            <span>Voulez-vous supprimer le contact : {props.field}</span>
          )}
          {props.type === "intervention" && (
            <span>
              Voulez-vous supprimer l'intervention sur : {props.field}
            </span>
          )}
          {props.type === "client" && clientLinks === null && !linksError && (
            <Spinner size="sm" ml={2} />
          )}
          {props.type === "client" && linksError && (
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              Impossible de vérifier les forfaits, contacts et sites de ce client :
              fermez et réessayez.
            </Alert>
          )}
          {deleteError && (
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              {deleteError}
            </Alert>
          )}
          {clientHasForfaits && (
            <Alert status="error" mt={4} borderRadius="md">
              <AlertIcon />
              Suppression impossible : ce client a {clientLinks.forfaits}{" "}
              forfait(s). Pour conserver l'historique des interventions,
              passez ses forfaits en inactif plutôt que de supprimer le client.
            </Alert>
          )}
          {props.type === "client" &&
            clientLinks !== null &&
            !clientHasForfaits &&
            clientLinks.contactIds.length > 0 && (
              <Text mt={4}>
                Ses {clientLinks.contactIds.length} contact(s) seront également
                supprimé(s).
              </Text>
            )}
          {props.type === "client" &&
            clientLinks !== null &&
            !clientHasForfaits &&
            clientLinks.siteIds.length > 0 && (
              <Text mt={4}>
                Ses {clientLinks.siteIds.length} site(s) seront également
                supprimé(s).
              </Text>
            )}
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={() => handleDelete(props.idDelete)}
            isDisabled={!canDelete}
            isLoading={isDeleting}
          >
            Supprimer
          </Button>
          <Button variant="ghost" onClick={props.onClose}>
            Fermer
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ModalDelete;
